import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, PencilSimple, Trash, ShieldCheck, Warning } from "@phosphor-icons/react";
import { formatApiError } from "@/utils/errorFormat";

/**
 * RolesCard — admin-only card rendered on the Users page. Lets an
 * operator add / edit / delete roles and toggle their per-permission
 * checkboxes without touching the database. Deleting a role prompts
 * for a fallback role that users on the deleted role will migrate to.
 */
const emptyForm = { name: "", label: "", description: "", permissions: {}, sort_order: 999 };

const RolesCard = ({ users = [], onRolesChanged }) => {
  const [roles, setRoles] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null=create; object=edit
  const [form, setForm] = useState(emptyForm);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [fallback, setFallback] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [rolesRes, catRes] = await Promise.all([
        axios.get(`${API}/roles`),
        axios.get(`${API}/permissions`),
      ]);
      setRoles(rolesRes.data || []);
      setCatalog(catRes.data || []);
    } catch (err) {
      toast.error(formatApiError(err, "Failed to load roles"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    // Default all perms to false — admin ticks what they want.
    const perms = {};
    catalog.forEach((c) => { perms[c.key] = false; });
    setEditing(null);
    setForm({ ...emptyForm, permissions: perms });
    setEditorOpen(true);
  };

  const openEdit = (r) => {
    setEditing(r);
    setForm({
      name: r.name,
      label: r.label || r.name,
      description: r.description || "",
      permissions: { ...(r.permissions || {}) },
      sort_order: r.sort_order ?? 999,
    });
    setEditorOpen(true);
  };

  const togglePerm = (key) => {
    setForm((f) => ({ ...f, permissions: { ...f.permissions, [key]: !f.permissions[key] } }));
  };

  const save = async () => {
    const name = (form.name || "").trim().toLowerCase();
    if (!name) { toast.error("Name required"); return; }
    setSaving(true);
    try {
      if (editing) {
        await axios.put(`${API}/roles/${editing.name}`, {
          label: form.label,
          description: form.description,
          permissions: form.permissions,
          sort_order: parseInt(form.sort_order) || 999,
        });
        toast.success("Role updated");
      } else {
        await axios.post(`${API}/roles`, {
          name,
          label: form.label || name,
          description: form.description,
          permissions: form.permissions,
          sort_order: parseInt(form.sort_order) || 999,
        });
        toast.success("Role created");
      }
      setEditorOpen(false);
      await load();
      if (onRolesChanged) onRolesChanged();
    } catch (err) {
      toast.error(formatApiError(err, "Save failed"));
    } finally {
      setSaving(false);
    }
  };

  const startDelete = (r) => {
    setDeleteTarget(r);
    // Default fallback: first non-target, non-empty role.
    const first = roles.find((x) => x.name !== r.name);
    setFallback(first ? first.name : "");
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    try {
      const params = fallback ? { fallback } : {};
      const res = await axios.delete(`${API}/roles/${deleteTarget.name}`, { params });
      const n = res.data?.migrated_users || 0;
      toast.success(n ? `Deleted — moved ${n} user(s) to ${fallback}` : "Role deleted");
      setDeleteTarget(null);
      setFallback("");
      await load();
      if (onRolesChanged) onRolesChanged();
    } catch (err) {
      toast.error(formatApiError(err, "Delete failed"));
    } finally {
      setSaving(false);
    }
  };

  const countUsers = (roleName) => users.filter((u) => u.role === roleName).length;

  return (
    <Card data-testid="roles-card">
      <CardHeader className="flex-row items-center justify-between gap-4 space-y-0">
        <div>
          <CardTitle className="font-heading flex items-center gap-2">
            <ShieldCheck size={20} weight="fill" className="text-primary" />
            Roles & Permissions
          </CardTitle>
          <CardDescription>
            Define who can void, refund, edit menu, view reports, and more. Custom roles are safe to add.
          </CardDescription>
        </div>
        <Button size="sm" onClick={openCreate} className="gap-1" data-testid="add-role-btn">
          <Plus size={16} /> New Role
        </Button>
      </CardHeader>
      <CardContent className="space-y-2">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="w-8 h-8 border-3 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : roles.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center">No roles yet</p>
        ) : (
          roles.map((r) => {
            const activePerms = Object.entries(r.permissions || {}).filter(([, v]) => v).length;
            const totalPerms = catalog.length || Object.keys(r.permissions || {}).length;
            const usersHere = countUsers(r.name);
            return (
              <div
                key={r.name}
                className="flex items-center gap-3 border border-border rounded-xl p-3"
                data-testid={`role-row-${r.name}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium">{r.label || r.name}</span>
                    <Badge variant="outline" className="font-mono text-xs">{r.name}</Badge>
                    {r.is_builtin && (
                      <Badge className="bg-blue-100 text-blue-800 border-0 text-xs">built-in</Badge>
                    )}
                    <span className="text-xs text-muted-foreground">
                      {activePerms}/{totalPerms} perms · {usersHere} user{usersHere === 1 ? "" : "s"}
                    </span>
                  </div>
                  {r.description && (
                    <p className="text-xs text-muted-foreground truncate">{r.description}</p>
                  )}
                </div>
                <Button variant="ghost" size="icon" onClick={() => openEdit(r)} data-testid={`edit-role-${r.name}`}>
                  <PencilSimple size={18} />
                </Button>
                <Button
                  variant="ghost" size="icon"
                  className="text-destructive"
                  onClick={() => startDelete(r)}
                  data-testid={`delete-role-${r.name}`}
                >
                  <Trash size={18} />
                </Button>
              </div>
            );
          })
        )}
      </CardContent>

      {/* Add / Edit Role Dialog */}
      <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="role-editor-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">
              {editing ? `Edit "${editing.label || editing.name}"` : "New Role"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2 overflow-y-auto flex-1">
            {!editing && (
              <div className="space-y-2">
                <Label>Role Name (lowercase, no spaces)</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, "") })}
                  placeholder="e.g. sous_chef"
                  data-testid="role-name-input"
                />
                <p className="text-xs text-muted-foreground">
                  This is what&apos;s saved on user records. Use the label below for display.
                </p>
              </div>
            )}
            <div className="space-y-2">
              <Label>Label</Label>
              <Input
                value={form.label}
                onChange={(e) => setForm({ ...form, label: e.target.value })}
                placeholder="Cook, Sous Chef, Host…"
                data-testid="role-label-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Description (optional)</Label>
              <Input
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="What can this role do?"
                data-testid="role-description-input"
              />
            </div>

            <div>
              <Label className="mb-2 block">Permissions</Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 border border-border rounded-xl p-3">
                {catalog.map((p) => (
                  <label
                    key={p.key}
                    className="flex items-start gap-2 cursor-pointer hover:bg-secondary/40 rounded px-2 py-1.5"
                    data-testid={`role-perm-row-${p.key}`}
                  >
                    <Checkbox
                      checked={!!form.permissions[p.key]}
                      onCheckedChange={() => togglePerm(p.key)}
                      data-testid={`role-perm-${p.key}`}
                    />
                    <div className="min-w-0">
                      <p className="text-sm">{p.label}</p>
                      <p className="text-[11px] text-muted-foreground font-mono">{p.key}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditorOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="save-role-btn">
              {saving ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Role Dialog */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) { setDeleteTarget(null); setFallback(""); } }}>
        <DialogContent className="sm:max-w-[440px]" data-testid="delete-role-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Delete role &quot;{deleteTarget?.label || deleteTarget?.name}&quot;?</DialogTitle>
          </DialogHeader>
          {deleteTarget && (
            <div className="space-y-3 py-2">
              {countUsers(deleteTarget.name) > 0 ? (
                <>
                  <div className="flex items-start gap-2 bg-orange-50 border border-orange-200 rounded-lg p-3 text-sm">
                    <Warning size={18} weight="fill" className="text-orange-600 shrink-0 mt-0.5" />
                    <div>
                      {countUsers(deleteTarget.name)} user(s) are currently on this role. Pick a fallback role &mdash; they&apos;ll be moved there.
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label>Move users to</Label>
                    <Select value={fallback} onValueChange={setFallback}>
                      <SelectTrigger data-testid="fallback-role-select"><SelectValue placeholder="Pick a role" /></SelectTrigger>
                      <SelectContent>
                        {roles.filter((r) => r.name !== deleteTarget.name).map((r) => (
                          <SelectItem key={r.name} value={r.name}>{r.label || r.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </>
              ) : (
                <p className="text-sm text-muted-foreground">No users are on this role, so no migration is needed.</p>
              )}
              {deleteTarget.is_builtin && (
                <p className="text-xs text-orange-700">
                  Heads-up: this is a built-in role. Deleting it will remove the corresponding dropdown option for staff.
                </p>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={confirmDelete}
              disabled={saving || (countUsers(deleteTarget?.name || "") > 0 && !fallback)}
              data-testid="confirm-delete-role-btn"
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default RolesCard;
